import type { K8SSnapshot } from "./useSnapshots";

// ctrl is gaining server-side ownership of deployability/root semantics
// (`root`, `root_ref`, `deployable`, `arch`, `phantom`, `value_score` on
// `K8SSnapshot`). The generated SDK type (`packages/sdk/src/types/ctrl.d.ts`)
// won't include these until that ctrl MR lands and openapi is regenerated, so
// we extend the type narrowly here rather than hand-editing the generated
// file. Every field is optional so a plain `K8SSnapshot` from an old ctrl
// still satisfies this type — every helper below falls back to the legacy
// label-based heuristic when the corresponding field is `undefined`.
export type SnapshotWithRoot = K8SSnapshot & {
  /** Server-computed: promoted chain root (`null.autos/snapshot-root=true`). */
  root?: boolean;
  /** Root group, e.g. "android-16". Only meaningful when `root` is true. */
  root_ref?: string;
  /** Server-computed deployability (ready + bootable image + successful job + not phantom). */
  deployable?: boolean;
  /** Normalized "amd64" | "arm64". */
  arch?: string;
  /** Data plane lost; never usable, regardless of `ready`/`deployable`. */
  phantom?: boolean;
  /** Controller retention score; higher = less likely to be evicted. Absent until scored. */
  value_score?: number | null;
};

/**
 * Legacy root label. Canonical source of truth for the string — anything
 * outside this package (e.g. the portal's snapshot-management constants)
 * should import this rather than redefining it, so the two can't drift.
 */
export const SNAPSHOT_ROOT_LABEL = "null.autos/snapshot-root";

// A deployable snapshot is a successful, ready AOSP *image* build. Identifying
// one comes down to separating it from the two things it sits next to in the
// `gitlab-runner` namespace:
//
//   1. CI caches (go-cache/js-cache/kaniko-cache) — the bulk of the namespace.
//      Labelled `type`/`project`, and never `arch`.
//   2. Intermediate chain stages of an AOSP pipeline (`sync`, `build-patch`).
//      These carry the build's project label but produce no bootable image,
//      and are likewise never `arch`-labelled.
//
// So the presence of an `arch` label is the signal: only a per-arch image job
// (`build-arm64` / `build-x86_64`) stamps it, and ctrl *requires* it to pick
// the target node (`kubernetes.io/arch` nodeSelector).
//
// NOTE: we require arch to be *present*, never a particular value. Both amd64
// and arm64 builds are deployable and ctrl resolves the arch server-side from
// the snapshot itself. Hardcoding a value here is what previously hid the
// arm64 (Graviton) builds entirely.
//
// The owning-build label is accepted under either key. `proj` is what the
// pipelines actually emit today — `templates/null-autos/android-16` sets
// `base_label_key: proj` / `NULL_SRC_LABELS_proj`, and its own resolve-snapshot
// job selects on `.labels.proj` + `.labels.arch`. `vehicle` is the manifest
// pipeline's intended key for per-vehicle builds (e.g. "lumen-s7-ivi"); nothing
// stamps it yet, so requiring it alone matched zero snapshots cluster-wide.
const BUILD_LABELS = ["vehicle", "proj"] as const;
const ARCH_LABEL = "arch";
const JOB_STATUS_LABEL = "null.autos/job-status";

export function isSnapshotDeployable(snapshot: SnapshotWithRoot | null): boolean {
  if (!snapshot) return false;

  // Trust the server once it tells us. `deployable` is only present on ctrl
  // builds that compute it server-side; a plain `boolean` check (rather than
  // truthiness) means we never mistake `undefined` (old ctrl) for `false`.
  if (typeof snapshot.deployable === "boolean") return snapshot.deployable;

  if (!snapshot.ready) return false;

  const labels = snapshot.labels ?? {};

  // Must belong to a build (not a CI cache) under either label convention.
  if (!BUILD_LABELS.some((key) => labels[key])) return false;
  // Must be a per-arch image job, not an intermediate sync/patch stage.
  if (!labels[ARCH_LABEL]) return false;
  // Must have completed — excludes canceled/failed builds.
  return labels[JOB_STATUS_LABEL] === "success";
}

/** True for arm64 snapshots. GPU isn't supported on arm64 in AWS yet. */
export function isSnapshotArm64(snapshot: SnapshotWithRoot | null | undefined): boolean {
  const arch = (snapshot?.arch ?? snapshot?.labels?.[ARCH_LABEL] ?? "").toLowerCase();
  return arch === "arm64" || arch === "aarch64";
}

export const ARM64_GPU_NOTE = "GPU on arm64 is coming soon.";

export function filterDeployableSnapshots(snapshots: SnapshotWithRoot[]): SnapshotWithRoot[] {
  return snapshots.filter(isSnapshotDeployable);
}

/** True when the snapshot is a promoted, shared, never-auto-evicted chain root. */
export function isSnapshotRoot(snapshot: SnapshotWithRoot | null): boolean {
  if (!snapshot) return false;
  if (typeof snapshot.root === "boolean") return snapshot.root;
  return SNAPSHOT_ROOT_LABEL in (snapshot.labels ?? {});
}

/**
 * Root group (e.g. "android-16"). Only ctrl builds that expose `root_ref`
 * report a value here — old ctrl deployments have no equivalent label, so
 * this returns `undefined` for them even when the snapshot is a root.
 */
export function snapshotRootRef(snapshot: SnapshotWithRoot | null): string | undefined {
  return snapshot?.root_ref?.trim() || undefined;
}

function snapshotCreatedAtMs(snapshot: SnapshotWithRoot): number {
  const t = snapshot.created_at ? Date.parse(snapshot.created_at) : NaN;
  return Number.isFinite(t) ? t : 0;
}

/**
 * Sort deployable snapshots the way the picker and server-side
 * `?deployable=true` both want them: roots first, then newest-first overall.
 */
export function sortDeployableSnapshots(
  snapshots: SnapshotWithRoot[],
): SnapshotWithRoot[] {
  return [...snapshots].sort((a, b) => {
    const rootDiff = Number(isSnapshotRoot(b)) - Number(isSnapshotRoot(a));
    if (rootDiff !== 0) return rootDiff;
    return snapshotCreatedAtMs(b) - snapshotCreatedAtMs(a);
  });
}

/**
 * The newest deployable root snapshot per `root_ref` group — the set a
 * picker should default to / highlight. Snapshots with no `root_ref` (old
 * ctrl, or a root ctrl hasn't grouped) are collapsed into a single unnamed
 * group. Returned newest-first.
 */
export function recommendedRootSnapshots(
  snapshots: SnapshotWithRoot[],
): SnapshotWithRoot[] {
  const newestByRef = new Map<string, SnapshotWithRoot>();

  for (const snapshot of snapshots) {
    if (!isSnapshotRoot(snapshot) || !isSnapshotDeployable(snapshot)) continue;

    const ref = snapshotRootRef(snapshot) ?? "";
    const current = newestByRef.get(ref);
    if (!current || snapshotCreatedAtMs(snapshot) > snapshotCreatedAtMs(current)) {
      newestByRef.set(ref, snapshot);
    }
  }

  return [...newestByRef.values()].sort(
    (a, b) => snapshotCreatedAtMs(b) - snapshotCreatedAtMs(a),
  );
}
